// src/pages/university-admin/InstitutionSetting.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Chip,
    Tabs,
    Tab,
    Slider,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Save,
    Settings,
    Timeline,
    Notifications,
    Api,
    Security,
    Edit,
    Close,
    CheckCircle,
    Warning,
    Info,
    ExpandMore,
    Restore,
    ToggleOn,
    ToggleOff,
    Add,
    Remove,
    Delete
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIInstitutionSettingsList,
    APIInstitutionSettingsUpdate,
    APIInstitutionSettingsResetDefault,
    APIInstitutionSettingsToggleStatus
} from '../../apiServices/APIs';
import { 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const InstitutionSetting = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [settings, setSettings] = useState({});
    const [activeCategory, setActiveCategory] = useState('general');
    const [editingSetting, setEditingSetting] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const institutionId = getUserDetails("institutionId");
    const adminId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchSettings();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.listById(APIInstitutionSettingsList, institutionId);
            if (result.success && result.data && isMounted.current) {
                setSettings(result.data);
            } else if (!result.success) {
                setError(result.message || "Failed to load settings");
            }
        } catch (err) {
            setError("Failed to load unit settings");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleUpdateSetting = async () => {
        if (!editingSetting) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('setting_id', editingSetting.id);
            formData.append('setting_value', JSON.stringify(editValue));
            
            const result = await apiService.update(APIInstitutionSettingsUpdate, formData);
            
            if (result && result.success) {
                showToast("Success", "Setting updated successfully", "success");
                fetchSettings();
                handleCloseEditDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update setting", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetToDefault = async (setting) => {
        if (!window.confirm('Are you sure you want to reset this setting to default values?')) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('setting_id', setting.id);
            
            const result = await apiService.update(APIInstitutionSettingsResetDefault, formData);
            
            if (result && result.success) {
                showToast("Success", "Setting reset to default", "success");
                fetchSettings();
            }
        } catch (error) {
            showToast("Error", "Failed to reset setting", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleStatus = async (setting) => {
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('admin_id', adminId);
            formData.append('setting_id', setting.id);
            
            const result = await apiService.update(APIInstitutionSettingsToggleStatus, formData);
            
            if (result && result.success) {
                showToast("Success", `Setting ${result.data.setting_status === 'active' ? 'activated' : 'deactivated'}`, "success");
                fetchSettings();
            }
        } catch (error) {
            showToast("Error", "Failed to toggle setting status", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenEditDialog = (setting) => {
        setEditingSetting(setting);
        setEditValue(setting.setting_value);
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setEditingSetting(null);
        setEditValue('');
    };

    const renderSettingEditor = () => {
        if (!editingSetting) return null;
        
        const value = editValue;
        
        switch (editingSetting.setting_key) {
            case 'workflow_config':
                return (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Milestones Configuration
                        </Typography>
                        {Object.entries(value.milestones || {}).map(([key, milestone]) => (
                            <Card key={key} sx={{ mb: 2 }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                        {key.replace(/_/g, ' ').toUpperCase()}
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <FormControlLabel 
                                                control={
                                                    <Switch 
                                                        checked={milestone.required} 
                                                        onChange={(e) => setEditValue({ 
                                                            ...value, 
                                                            milestones: { 
                                                                ...value.milestones, 
                                                                [key]: { ...milestone, required: e.target.checked } 
                                                            } 
                                                        })} 
                                                    />
                                                } 
                                                label="Required" 
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label="Order" 
                                                type="number" 
                                                size={isMobile ? "small" : "medium"}
                                                value={milestone.order} 
                                                onChange={(e) => setEditValue({ 
                                                    ...value, 
                                                    milestones: { 
                                                        ...value.milestones, 
                                                        [key]: { ...milestone, order: parseInt(e.target.value) } 
                                                    } 
                                                })} 
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField 
                                                fullWidth 
                                                label="Grace Period (days)" 
                                                type="number" 
                                                size={isMobile ? "small" : "medium"}
                                                value={milestone.grace_days} 
                                                onChange={(e) => setEditValue({ 
                                                    ...value, 
                                                    milestones: { 
                                                        ...value.milestones, 
                                                        [key]: { ...milestone, grace_days: parseInt(e.target.value) } 
                                                    } 
                                                })} 
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                );
                
            case 'deadline_rules':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Default Review Deadline (days)" 
                                type="number" 
                                value={value.default_review_deadline_days} 
                                onChange={(e) => setEditValue({ ...value, default_review_deadline_days: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Grace Period (days)" 
                                type="number" 
                                value={value.grace_period_days} 
                                onChange={(e) => setEditValue({ ...value, grace_period_days: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Max Extension (days)" 
                                type="number" 
                                value={value.max_extension_days} 
                                onChange={(e) => setEditValue({ ...value, max_extension_days: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Auto Escalate After (days)" 
                                type="number" 
                                value={value.auto_escalate_after_days} 
                                onChange={(e) => setEditValue({ ...value, auto_escalate_after_days: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                    </Grid>
                );
                
            case 'plagiarism_thresholds':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Warning Threshold: {value.warning_threshold}%
                            </Typography>
                            <Slider 
                                value={value.warning_threshold} 
                                onChange={(e, v) => setEditValue({ ...value, warning_threshold: v })} 
                                min={0} 
                                max={100} 
                                step={1} 
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Review Threshold: {value.review_threshold}%
                            </Typography>
                            <Slider 
                                value={value.review_threshold} 
                                onChange={(e, v) => setEditValue({ ...value, review_threshold: v })} 
                                min={0} 
                                max={100} 
                                step={1} 
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Rejection Threshold: {value.rejection_threshold}%
                            </Typography>
                            <Slider 
                                value={value.rejection_threshold} 
                                onChange={(e, v) => setEditValue({ ...value, rejection_threshold: v })} 
                                min={0} 
                                max={100} 
                                step={1} 
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        checked={value.auto_check_enabled} 
                                        onChange={(e) => setEditValue({ ...value, auto_check_enabled: e.target.checked })} 
                                    />
                                } 
                                label="Auto-check enabled" 
                            />
                        </Grid>
                    </Grid>
                );
                
            case 'supervisor_workload_limits':
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Max Primary Students" 
                                type="number" 
                                value={value.max_primary_students} 
                                onChange={(e) => setEditValue({ ...value, max_primary_students: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Max Co-supervisor Students" 
                                type="number" 
                                value={value.max_co_supervisor_students} 
                                onChange={(e) => setEditValue({ ...value, max_co_supervisor_students: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Max Total Students" 
                                type="number" 
                                value={value.max_total_students} 
                                onChange={(e) => setEditValue({ ...value, max_total_students: parseInt(e.target.value) })}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Warning at Percentage" 
                                type="number" 
                                value={value.warning_at_percentage} 
                                onChange={(e) => setEditValue({ ...value, warning_at_percentage: parseInt(e.target.value) })} 
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                    </Grid>
                );
                
            default:
                return (
                    <TextField 
                        fullWidth 
                        multiline 
                        rows={6} 
                        value={JSON.stringify(value, null, 2)} 
                        onChange={(e) => setEditValue(JSON.parse(e.target.value))}
                        size={isMobile ? "small" : "medium"}
                    />
                );
        }
    };

    if (loading) {
        return <LoadingState message="Loading Unit Settings..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchSettings} title="Unable to Load Settings" variant="research" fullPage retryText="Retry Loading" />;
    }

    const categories = Object.keys(settings);

    return (
        <>
            <FullWidthContainer>
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <InstitutionAdminBar notificationCount={78} />
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <Settings /> Unit Settings
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="info" 
                            startIcon={<RefreshIcon />} 
                            onClick={fetchSettings} 
                            sx={{ 
                                textTransform: 'none',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                            size={isMobile ? "small" : "medium"}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Paper>
            </Box>
                {/* Settings Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                }}>
                    <Tabs
                        value={activeCategory}
                        onChange={(e, v) => setActiveCategory(v)}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                    >
                        {categories.map(category => (
                            <Tab 
                                key={category} 
                                value={category} 
                                label={settings[category]?.info?.name || category} 
                                icon={settings[category]?.info?.icon && (
                                    <Box component="span" sx={{ mr: { xs: 0.5, sm: 1 } }}>
                                        {settings[category].info.icon === 'Settings' ? <Settings fontSize="small" /> : 
                                         settings[category].info.icon === 'Timeline' ? <Timeline fontSize="small" /> : 
                                         settings[category].info.icon === 'Notifications' ? <Notifications fontSize="small" /> : 
                                         settings[category].info.icon === 'Api' ? <Api fontSize="small" /> : <Security fontSize="small" />}
                                    </Box>
                                )} 
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        {settings[activeCategory]?.settings?.map((setting, index) => (
                            <Zoom in={true} key={setting.id} style={{ transitionDelay: `${index * 50}ms` }}>
                                <Card sx={{ 
                                    mb: 3, 
                                    position: 'relative',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)' }
                                }}>
                                    <CardHeader
                                        title={
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}>
                                                {setting.setting_key.replace(/_/g, ' ').toUpperCase()}
                                            </Typography>
                                        }
                                        subheader={
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                {setting.description}
                                            </Typography>
                                        }
                                        action={
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                <Chip 
                                                    label={setting.setting_status.toUpperCase()} 
                                                    size="small" 
                                                    color={setting.status_color}
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                                <Chip 
                                                    label={setting.validation_status.toUpperCase()} 
                                                    size="small" 
                                                    color={setting.validation_status_color}
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </Box>
                                        }
                                        sx={{ 
                                            '& .MuiCardHeader-content': { 
                                                overflow: 'hidden',
                                                mr: { xs: 0, sm: 2 }
                                            },
                                            '& .MuiCardHeader-action': {
                                                mt: { xs: 0.5, sm: 0 },
                                                alignSelf: { xs: 'flex-start', sm: 'center' }
                                            }
                                        }}
                                    />
                                    <CardContent>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                Current Configuration:
                                            </Typography>
                                            <Paper sx={{ 
                                                bgcolor: theme.palette.grey[50], 
                                                p: { xs: 1, sm: 1.5 }, 
                                                borderRadius: 2,
                                                overflow: 'auto'
                                            }}>
                                                <pre style={{ 
                                                    margin: 0, 
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    fontFamily: 'monospace'
                                                }}>
                                                    {JSON.stringify(setting.setting_value, null, 2)}
                                                </pre>
                                            </Paper>
                                        </Box>
                                        
                                        {setting.validation_errors && setting.validation_errors.length > 0 && (
                                            <Alert severity="warning" sx={{ mb: 2, p: { xs: 1, sm: 1.5 } }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    Validation Issues:
                                                </Typography>
                                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                    {setting.validation_errors.map((err, i) => (
                                                        <li key={i} style={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{err}</li>
                                                    ))}
                                                </ul>
                                            </Alert>
                                        )}
                                        
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'flex-end', 
                                            gap: 1, 
                                            mt: 2,
                                            flexWrap: 'wrap'
                                        }}>
                                            <Tooltip title="Edit Setting">
                                                <IconButton 
                                                    onClick={() => handleOpenEditDialog(setting)} 
                                                    color="primary" 
                                                    disabled={!setting.is_editable}
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reset to Default">
                                                <IconButton 
                                                    onClick={() => handleResetToDefault(setting)} 
                                                    color="warning"
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    <Restore />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={setting.setting_status === 'active' ? 'Deactivate' : 'Activate'}>
                                                <IconButton 
                                                    onClick={() => handleToggleStatus(setting)} 
                                                    color={setting.setting_status === 'active' ? 'error' : 'success'}
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    {setting.setting_status === 'active' ? <ToggleOff /> : <ToggleOn />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Zoom>
                        ))}
                        
                        {(!settings[activeCategory]?.settings || settings[activeCategory].settings.length === 0) && (
                            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
                                <Settings sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No settings in this category
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Edit Setting Dialog */}
                <Dialog 
                    open={openEditDialog} 
                    onClose={handleCloseEditDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2
                    }}>
                        Edit Setting: {editingSetting?.setting_key?.replace(/_/g, ' ').toUpperCase()}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ pt: 1 }}>
                            {renderSettingEditor()}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 }, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Button onClick={handleCloseEditDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpdateSetting} 
                            variant="contained" 
                            disabled={actionLoading} 
                            startIcon={actionLoading ? <CircularProgress size={20} /> : <Save />}
                            size={isMobile ? "small" : "medium"}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default InstitutionSetting;